import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiHome,
  FiPackage,
  FiShoppingBag,
  FiCalendar,
  FiUsers,
  FiBox,
  FiMenu,
  FiX,
} from 'react-icons/fi';

/**
 * AdminSidebar Component
 * Props: menuItems array
 * Features:
 * - Collapsible
 * - Active section highlight
 * - Animations: slide-in/out
 */

const AdminSidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();

  const menuItems = [
    { title: 'Dashboard', icon: FiHome, path: '/admin' },
    { title: 'Products', icon: FiPackage, path: '/admin/products' },
    { title: 'Orders', icon: FiShoppingBag, path: '/admin/orders' },
    { title: 'Bookings', icon: FiCalendar, path: '/admin/bookings' },
    { title: 'B2B Accounts', icon: FiUsers, path: '/admin/b2b-accounts' },
    { title: 'Inventory', icon: FiBox, path: '/admin/inventory' },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-primary-800 text-white rounded-lg"
      >
        {isCollapsed ? <FiMenu className="text-2xl" /> : <FiX className="text-2xl" />}
      </button>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          width: isCollapsed ? '80px' : '256px',
        }}
        transition={{ duration: 0.3 }}
        className={`
          bg-primary-800 text-white min-h-screen
          fixed lg:relative z-40
          ${isCollapsed ? 'hidden lg:block' : 'block'}
        `}
      >
        <div className="p-6">
          {/* Logo */}
          <div className="flex items-center space-x-2 mb-8">
            {!isCollapsed && (
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xl font-bold"
              >
                Admin Panel
              </motion.h1>
            )}
          </div>

          {/* Toggle Button (Desktop) */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:block w-full mb-4 p-2 hover:bg-primary-700 rounded-lg transition-colors duration-300"
          >
            {isCollapsed ? <FiMenu className="mx-auto" /> : <FiX className="mx-auto" />}
          </button>

          {/* Menu Items */}
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center space-x-3 p-3 rounded-lg
                    transition-all duration-300
                    ${
                      active
                        ? 'bg-secondary-500 text-white'
                        : 'hover:bg-primary-700 text-gray-300'
                    }
                  `}
                  title={item.title}
                >
                  <Icon className="text-xl flex-shrink-0" />
                  {!isCollapsed && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="font-medium"
                    >
                      {item.title}
                    </motion.span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User Info */}
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute bottom-0 left-0 right-0 p-6 border-t border-primary-700"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-secondary-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">A</span>
              </div>
              <div>
                <p className="text-sm font-medium">Admin User</p>
                <p className="text-xs text-gray-400">admin@example.com</p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.aside>
    </>
  );
};

export default AdminSidebar;

